import { useEffect, useEffectEvent, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Product } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Form } from '@nucleus-commerce/ui/components/form';

import { fromProduct } from '../api/mappers';
import { AttributeValuesSection } from '../components/attribute-values-section';
import { ConfiguratorSection } from '../components/configurator-section';
import { CustomizationsSection } from '../components/customizations-section';
import { pendingFaqSuggestions } from '../components/faq-suggestions';
import { OptionsSection } from '../components/options-section';
import { EditOnMasterLink, SharedChip } from '../components/product-scope';
import {
  ProductFulfilmentSection,
  ProductOrganisationSection,
} from '../components/product-side-rail';
import { RelationsSection } from '../components/relations-section';
import { ShippingSection } from '../components/shipping-section';
import { SummaryFaqFields } from '../components/summary-faq-section';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { useProductEditor, type ProductEditor } from './use-product-editor';
import { ProductScopeProvider, type ProductScope } from './use-product-scope';
import { useSaveProduct } from './use-save-product';
import { useVariants } from './use-variants';

export interface UseSharedProductSectionsOptions {
  product: Product;
  /** The store and store count the cards name; shared and read-only are this hook's to say. */
  scope: Omit<ProductScope, 'readOnly' | 'shared'>;
  /** After a save lands, so the host can refresh its own copy of the product. */
  onSaved?: (product: Product) => void;
}

/** Each is one card or a few, in the context they need, for the host to place in its own order. */
export interface SharedProductSections {
  /** The master product's form, for a card the host shares between it and its own form. */
  form: ProductEditor['form'];
  /** A failed save of the master product; render it once, near the top. */
  saveError: ReactNode;
  attributes: ReactNode;
  options: ReactNode;
  diamond: ReactNode;
  related: ReactNode;
  customizations: ReactNode;
  fulfilment: ReactNode;
  shipping: ReactNode;
  /** The Organization card, with the host's own fields under the shared ones. */
  organisation: (children?: ReactNode) => ReactNode;
  /** The Summary and FAQ tab of the Search and answer engines card. */
  answers: ReactNode;
  /** Beside that tab's name: the product type's suggestions still to take. */
  answersBadge: ReactNode;
  /** For the host's own header; null without a type or until it loads. */
  productTypeName: string | null;
}

/** Master fields a store's page has no input for, so an error on one has nowhere to show. */
const NO_INPUT_HERE = [
  ['title', 'fields.title'],
  ['subtitle', 'fields.subtitle'],
  ['descriptionHtml', 'fields.description'],
  ['handle', 'fields.handle'],
  ['status', 'sections.status'],
  ['seo', 'searchCard.tabListing'],
  ['metadata', 'metadata.title'],
] as const;

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The master product's cards for a page about something else, a store's product page: the same
 * components, validation and save as the product page, in a form of their own on the save bar. A
 * change here is a change to the product, so it reaches every store; each card says so with its
 * Shared chip. The host decides where each group sits, and never remounts this, since it holds
 * unsaved edits: a save or a newer product takes effect in place and spares what changed meanwhile.
 */
export function useSharedProductSections({
  product,
  scope,
  onSaved,
}: UseSharedProductSectionsOptions): SharedProductSections {
  const { t } = useTranslation('products');
  const variants = useVariants(product.id);
  const save = useSaveProduct(product.id, onSaved ? { onSaved } : {});
  const editor = useProductEditor({
    defaultValues: fromProduct(product),
    onSubmit: save.onSubmit,
    isSubmitting: save.isPending,
    reseed: (saved) => fromProduct(saved as Product),
    savedSkuRoot: product.skuRoot,
  });
  const { form, productType, readOnly } = editor;

  // A refetch with a newer product, or a save elsewhere on the page. The editor's own save re-seeds
  // when it's done, so the product changing during one is left to it.
  const seenProduct = useRef(product);
  const followProduct = useEffectEvent(() => {
    if (save.isPending) return;
    const next = fromProduct(product);
    const saved = form.formState.defaultValues;
    if (sameValue(next, saved)) return;
    const current = form.getValues();
    form.reset(next);
    for (const name of Object.keys(current) as Array<keyof ProductFormInput>) {
      if (!sameValue(current[name], saved?.[name])) {
        form.setValue(name, current[name], { shouldDirty: true });
      }
    }
  });
  useEffect(() => {
    if (seenProduct.current === product) return;
    seenProduct.current = product;
    followProduct();
  }, [product]);

  const bound = (children: ReactNode) => (
    <ProductScopeProvider value={{ ...scope, shared: true, readOnly }}>
      <Form {...form}>{children}</Form>
    </ProductScopeProvider>
  );
  const inForm = (children: ReactNode) =>
    bound(
      <form onSubmit={editor.submit} noValidate>
        {children}
      </form>,
    );

  const { errors } = form.formState;
  const noInputErrors = NO_INPUT_HERE.flatMap(([name, label]) => {
    const message = errors[name]?.message;
    if (errors[name] === undefined) return [];
    return [
      `${t(label)}: ${typeof message === 'string' ? t(message) : t('scope.sharedSaveFailed')}`,
    ];
  });
  const suggested = pendingFaqSuggestions(form.watch('faq'), productType ?? null).length;

  return {
    form,
    saveError:
      editor.rootError || noInputErrors.length > 0
        ? bound(
            <Alert variant="destructive" role="alert">
              <AlertTitle>{t('scope.sharedSaveFailed')}</AlertTitle>
              <AlertDescription>
                {editor.rootError ? <p>{editor.rootError}</p> : null}
                {noInputErrors.length > 0 ? (
                  <p>
                    {noInputErrors.join(' · ')} <EditOnMasterLink />
                  </p>
                ) : null}
              </AlertDescription>
            </Alert>,
          )
        : null,
    attributes: inForm(
      <AttributeValuesSection
        productType={productType}
        attributesById={editor.attributesById}
        isLoading={editor.productTypeLoading}
      />,
    ),
    options: inForm(
      <OptionsSection
        attributes={editor.attributes}
        attributesById={editor.attributesById}
        saved={product}
        variants={variants.data}
      />,
    ),
    diamond: bound(
      <Can permission="configurator.read">
        <ConfiguratorSection product={product} />
      </Can>,
    ),
    related: bound(<RelationsSection product={product} />),
    customizations: bound(<CustomizationsSection />),
    fulfilment: bound(<ProductFulfilmentSection />),
    shipping: bound(<ShippingSection />),
    // No <form> of its own: the host's fields inside bring theirs.
    organisation: (children) =>
      bound(
        <ProductOrganisationSection productTypes={editor.productTypes} saved={product}>
          {children}
        </ProductOrganisationSection>,
      ),
    answers: bound(
      <div className="flex flex-col gap-3">
        {readOnly ? (
          <div>
            <SharedChip />
          </div>
        ) : (
          <p className="text-muted-foreground flex items-center gap-2 text-[13px]">
            <SharedChip />
            {t('searchCard.sharedAnswers')}
          </p>
        )}
        <SummaryFaqFields productType={productType ?? null} />
      </div>,
    ),
    answersBadge: suggested > 0 ? t('faq.suggestedCount', { count: suggested }) : null,
    productTypeName: productType?.name ?? null,
  };
}
